import React, { FC, memo, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import reducerSlice, {
  clearCurrentFilters,
  clearCurrentTextBoxValues,
  clearFilter,
  initialState,
  onFetchSuccessTextBoxFilterData,
  resetFilters,
  setCurrentSortItem,
  setCurrentTextBoxIsLoading,
  setFilter,
  setFilterItems,
  setSortItemsData
} from './redux/slices';
import { FilterComponentTypes } from '../../../Filters/const';
import { TCurrentFilter, TFilters, TSort } from '../../../../commonTypes/filters';
import { TTextBoxFilterResponse } from '../../../Filters/redux/types';
import axiosInstance, { CancelToken } from '../../../../../client/core/interceptor';
import { AxiosResponse } from 'axios';
import { ModalFilter } from '../index';
import { groupFilters } from '../../../Filters/utils';

type TProps = {
  filterData: TFilters;
  currentAccountId: string;
  onFilterChange: (data: TCurrentFilter[]) => void;
  onSortChange: (data: TSort) => void;
  isOpen: boolean;
  url: string;
  requestURL: string;
  requestPage: string;
  initialFilterValue?: TCurrentFilter;
  errorMassage?: string;
};

const ModalFiltersNativeWrapper: FC<TProps> = ({
  filterData,
  currentAccountId,
  onFilterChange,
  onSortChange,
  isOpen: isModalOpen,
  url,
  requestURL,
  requestPage,
  initialFilterValue = null,
  errorMassage = ''
}) => {
  const [state, dispatch] = useReducer(reducerSlice, initialState);
  const {
    modal: {
      filterItems: filters,
      currentFilterItems,
      currentTextBoxIsLoadingStates: isLoadingStates,
      currentTextBoxItems,
      sortItems,
      currentSortItem
    }
  } = state;
  const checkboxRequestData = {
    requestURL,
    requestPage
  };
  const [isOpen, setIsOpen] = useState(false);
  const [isFirstInit, setIsFirstInit] = useState(true);
  const [source, setSource] = useState(null);

  // Set filters on first loading
  useEffect(() => {
    if (isFirstInit && filterData !== null && filterData !== undefined) {
      if (Array.isArray(filterData.filterItems)) {
        dispatch(setFilterItems({ filterItems: filterData.filterItems, type: FilterComponentTypes.Modal }));
      }
      if (Array.isArray(filterData.sortItems)) {
        dispatch(setSortItemsData({ sortItems: filterData.sortItems, type: FilterComponentTypes.Modal }));
      }
      if (initialFilterValue) {
        dispatch(setFilter({ newFilterItem: initialFilterValue, type: FilterComponentTypes.Modal }));
      }
      setIsFirstInit(false);
    }
  }, [isFirstInit, filterData, initialFilterValue]);

  useEffect(() => {
    if (!isModalOpen) {
      resetFilters(FilterComponentTypes.Modal);
    }
  }, [isModalOpen]);

  // On filters change
  useEffect(() => {
    if (isModalOpen && !isFirstInit) {
      onFilterChange(currentFilterItems);
    }
  }, [currentFilterItems]);

  // On sorting change
  useEffect(() => {
    if (isModalOpen) {
      onSortChange(currentSortItem);
    }
  }, [currentSortItem]);

  const handleSwitchFilter = () => setIsOpen(!isOpen);
  const handleClearAllFilters = () => {
    dispatch(clearCurrentFilters(FilterComponentTypes.Modal));
    dispatch(
      clearCurrentTextBoxValues({
        textBoxItems: currentFilterItems.map((item) => item.internalName),
        type: FilterComponentTypes.Modal
      })
    );
  };
  const handleSetFilter = (data) => dispatch(setFilter({ newFilterItem: data, type: FilterComponentTypes.Modal }));
  const handleSortChange = (data) =>
    dispatch(setCurrentSortItem({ newSortItem: data, type: FilterComponentTypes.Modal }));
  const handleClearFilter = (data) =>
    dispatch(clearFilter({ filterInternalName: data, type: FilterComponentTypes.Modal }));
  const handleClearCurrentTextBoxValues = (data) =>
    dispatch(clearCurrentTextBoxValues({ textBoxItems: data, type: FilterComponentTypes.Modal }));
  const groupedFilters = useMemo(() => groupFilters(filters), [filters]);

  const handleDataTextBoxFilterAction = (internalName, searchValue) => {
    const axiosSource = CancelToken.source();
    setSource(axiosSource);

    const apiUrl = `${url}${checkboxRequestData.requestURL}?InternalName=${internalName}&SearchValue=${searchValue}&AccountId=${currentAccountId}&RequestPage=${checkboxRequestData.requestPage}`;

    dispatch(
      setCurrentTextBoxIsLoading({
        internalName: internalName,
        isLoading: true,
        type: FilterComponentTypes.Modal
      })
    );

    axiosInstance
      .get(apiUrl, { cancelToken: axiosSource.token })
      .then((response: AxiosResponse<TTextBoxFilterResponse>) => {
        const currentTextBox = {
          internalName: internalName,
          inputValue: searchValue,
          values: response.data.data
        };
        dispatch(
          setCurrentTextBoxIsLoading({
            internalName: internalName,
            isLoading: false,
            type: FilterComponentTypes.Modal
          })
        );
        dispatch(onFetchSuccessTextBoxFilterData({ currentTextBox, type: FilterComponentTypes.Modal }));
      })
      .catch((e) => {
        dispatch(
          setCurrentTextBoxIsLoading({
            internalName: internalName,
            isLoading: false,
            type: FilterComponentTypes.Modal
          })
        );
        // TODO: add error handler
      });
  };

  const handleCancelDataTextBoxFilterAction = (internalName: string) => {
    if (!source) {
      return;
    }

    source.cancel();

    setCurrentTextBoxIsLoading({
      internalName: internalName,
      isLoading: false,
      type: FilterComponentTypes.Modal
    })
  };

  return (
    <ModalFilter
      isOpen={isOpen}
      currentTextBoxItems={currentTextBoxItems}
      filters={filters}
      groupedFilters={groupedFilters}
      currentFilterItems={currentFilterItems}
      sortItems={sortItems}
      currentSortItem={currentSortItem}
      isLoadingStates={isLoadingStates}
      onSwitchFilter={handleSwitchFilter}
      onClearAllFilters={handleClearAllFilters}
      onSetFilter={handleSetFilter}
      onClearFilter={handleClearFilter}
      onClearCurrentTextBoxValues={handleClearCurrentTextBoxValues}
      onFetchDataTextBoxFilterAction={handleDataTextBoxFilterAction}
      onFetchCancel={handleCancelDataTextBoxFilterAction}
      onSortChange={handleSortChange}
      errorMassage={errorMassage}
    />
  );
};

export default memo(ModalFiltersNativeWrapper);
